import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Portal,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { TopPanel } from "@jugl-web/domain-resources/files/providers/FilePreviewProvider/components/TopPanel";
import { FC, Fragment, useEffect, useMemo } from "react";
import { MentionTextareaProps } from "@jugl-web/domain-resources/common/components/RichTextarea";
import EditorCanvas from "./components/EditorCanvas";
import EditorToolbar from "./components/EditorToolbar";
import { ImageEditorProvider } from "./components/EditorProvider";

type ImageEditorProps = {
  file?: File;
  caption?: string;
  isOpen?: boolean;
  mentions?: MentionTextareaProps["mentions"];
  hideCaptionInput?: boolean;
  onDelete?: () => void;
  onEdit: ({ file, caption }: { file?: File; caption?: string }) => void;
  onSave: () => void;
  onClose: () => void;
};

const ImageEditor: FC<ImageEditorProps> = ({
  file,
  onDelete,
  caption,
  mentions,
  hideCaptionInput,
  onClose,
  onSave,
  onEdit,
  isOpen,
}) => {
  const fileUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : undefined),
    [file]
  );

  useEffect(() => {
    if (!fileUrl) return () => {};

    return () => URL.revokeObjectURL(fileUrl);
  }, [fileUrl]);

  return (
    <Portal>
      <Transition show={isOpen} as={Fragment}>
        <Dialog unmount as="div" onClose={onClose}>
          <TransitionChild
            as={Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 z-50 bg-[rgba(14,14,14,0.66)] backdrop-blur-[1px]" />
          </TransitionChild>

          <DialogPanel className="fixed top-0 right-0 bottom-0 left-0 z-[1300] flex h-screen w-screen flex-col overflow-hidden">
            <TransitionChild
              as={Fragment}
              enter="transition duration-200"
              enterFrom="opacity-0 -translate-y-full"
              enterTo="opacity-100 translate-y-0"
              leave="transition duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 -translate-y-full"
            >
              <div className="w-full shrink-0">
                <TopPanel
                  fileName="Edit Image"
                  onClose={onClose}
                  onDelete={onDelete}
                />
              </div>
            </TransitionChild>
            <TransitionChild
              as={Fragment}
              enter="transition duration-200"
              enterFrom="opacity-0 scale-90"
              enterTo="opacity-100 scale-100"
              leave="transition duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-90"
            >
              <div className="flex grow flex-col items-center justify-center gap-4 pt-2">
                <ImageEditorProvider
                  onEdit={onEdit}
                  onSave={onSave}
                  caption={caption || ""}
                  closeModal={onClose}
                  mentions={mentions}
                >
                  {fileUrl && (
                    <>
                      <EditorCanvas url={fileUrl} />
                      <EditorToolbar hideCaptionInput={hideCaptionInput} />
                    </>
                  )}
                </ImageEditorProvider>
              </div>
            </TransitionChild>
          </DialogPanel>
        </Dialog>
      </Transition>
    </Portal>
  );
};

export default ImageEditor;
